/**
 * Discount Popup Handler
 * Handles discount code detection from URLs and price updates after OptiMonk popup interactions
 */

class DiscountPopupHandler {
  constructor() {
    this.discountCode = null;
    this.originalPrice = null;
    this.discountedPrice = null;
    this.discountPercent = null; // Store discount percentage for variant persistence
    this.isProductPage = false;
    this.buttonWasClicked = false;
    
    this.init();
  }

  async init() {
    // Extract discount code FIRST (before other checks)
    // This ensures we capture it even if we're on discount page before redirect
    this.extractDiscountCode();
    
    // Detect if we're on a product page
    this.isProductPage = document.body.classList.contains('template-product') || 
                        document.querySelector('[data-section-type="product"]') !== null ||
                        document.querySelector('product-form') !== null ||
                        window.location.pathname.includes('/products/');
    
    // Check if button was clicked on previous page (stored in sessionStorage)
    this.buttonWasClicked = sessionStorage.getItem('sokito_discount_button_clicked') === 'true';
    
    // Set up OptiMonk button listener
    this.setupOptiMonkListener();
    
    // Set up variant change listener for discount persistence
    if (this.isProductPage) {
      this.setupVariantChangeListener();
    }
    
    // Check for stored discount state (user previously applied discount in this session)
    if (this.isProductPage) {
      this.restoreDiscountState().catch((error) => {
        console.warn('Error restoring discount state:', error);
      });
    }
    
    // Only auto-apply discount if button was clicked and we're on product page with discount code
    // This prevents applying discount on initial page load before button click
    if (this.isProductPage && this.discountCode && this.buttonWasClicked) {
      // Clear the flag since we're applying the discount now
      sessionStorage.removeItem('sokito_discount_button_clicked');
      
      // Small delay to ensure page is fully loaded
      setTimeout(() => {
        this.autoApplyDiscount();
      }, 500);
    }
  }

  /**
   * Extract discount code from URL parameter
   * Handles format: ?DISCOUNTCODE (code as direct parameter, no key)
   * Examples: ?BRAZIL20, ?20deal, ?SAVE15, ?SUMMER2024
   * Also handles: ?DISCOUNTCODE&other=params
   * 
   * @returns {string|null} The discount code extracted from URL, or null if not found
   */
  extractDiscountCodeFromURL() {
    const search = window.location.search;
    if (!search || search.length <= 1) return null;
    
    // Remove leading ? and get the first parameter
    // Format: ?DISCOUNTCODE or ?DISCOUNTCODE&preview_theme_id=...
    const firstParam = search.substring(1).split('&')[0];
    
    // Only accept if it's NOT a key=value format (should be just the code)
    // Reject formats like ?code=DISCOUNTCODE, only accept ?DISCOUNTCODE
    if (firstParam.includes('=')) return null;
    
    // Validate: discount codes should be alphanumeric with possible dashes/underscores
    // Length constraints: 2-50 characters (covers common discount code formats)
    // Examples that will match: BRAZIL20, 20deal, SAVE-15, SUMMER_2024, XMAS25
    if (firstParam && /^[A-Za-z0-9_-]+$/.test(firstParam) && firstParam.length >= 2 && firstParam.length <= 50) {
      return firstParam;
    }
    
    return null;
  }
  
  /**
   * Extract discount code - simplified for new flow
   * Code comes from URL parameter when button is clicked
   * Works with any discount code format (not specific to any code name)
   */
  extractDiscountCode() {
    // Check URL parameter (format: ?DISCOUNTCODE - any valid code)
    const code = this.extractDiscountCodeFromURL();
    if (code) {
      this.discountCode = code;
      this.storeDiscountCode(this.discountCode);
      return;
    }
  }

  /**
   * Store discount code (simplified to 2 layers: sessionStorage + localStorage)
   */
  storeDiscountCode(code) {
    try {
      const timestamp = Date.now().toString();
      sessionStorage.setItem('sokito_discount_code', code);
      sessionStorage.setItem('sokito_discount_timestamp', timestamp);
      localStorage.setItem('sokito_discount_code', code);
      localStorage.setItem('sokito_discount_timestamp', timestamp);
    } catch (e) {
      console.warn('Could not store discount code:', e);
    }
  }

  /**
   * Retrieve stored discount code (simplified to 2 layers)
   */
  getStoredDiscountCode() {
    try {
      // Check sessionStorage first (30 min)
      let code = sessionStorage.getItem('sokito_discount_code');
      let timestamp = sessionStorage.getItem('sokito_discount_timestamp');
      if (code && timestamp && (Date.now() - parseInt(timestamp) < 30 * 60 * 1000)) {
        return code;
      }
      
      // Check localStorage (7 days)
      code = localStorage.getItem('sokito_discount_code');
      timestamp = localStorage.getItem('sokito_discount_timestamp');
      if (code && timestamp && (Date.now() - parseInt(timestamp) < 7 * 24 * 60 * 60 * 1000)) {
        // Restore to sessionStorage for current session
        sessionStorage.setItem('sokito_discount_code', code);
        sessionStorage.setItem('sokito_discount_timestamp', Date.now().toString());
        return code;
      }
      
      // Clean up expired entries
      if (code) {
        localStorage.removeItem('sokito_discount_code');
        localStorage.removeItem('sokito_discount_timestamp');
      }
    } catch (e) {
      console.warn('Could not retrieve discount code from storage:', e);
    }
    
    return null;
  }

  /**
   * Clear stored discount code
   */
  clearStoredDiscountCode() {
    try {
      sessionStorage.removeItem('sokito_discount_code');
      sessionStorage.removeItem('sokito_discount_timestamp');
      localStorage.removeItem('sokito_discount_code');
      localStorage.removeItem('sokito_discount_timestamp');
    } catch (e) {
      console.warn('Could not clear discount code from storage:', e);
    }
  }

  /**
   * Set up event listener for OptiMonk discount-refresh button
   * Note: We do NOT prevent default behavior - OptiMonk handles redirect/navigation
   * When button is clicked, we set a flag so discount applies after redirect
   */
  setupOptiMonkListener() {
    // Track when button is clicked - don't interfere with OptiMonk's redirect
    // The flag tells us to apply discount after redirect on next page load
    document.addEventListener('click', (event) => {
      const clickedElement = event.target;
      const isDiscountButton = clickedElement.classList?.contains('discount-refresh') || 
                                clickedElement.closest?.('.discount-refresh');
      
      if (isDiscountButton) {
        // Set flag so we know to apply discount after redirect
        try {
          sessionStorage.setItem('sokito_discount_button_clicked', 'true');
          this.buttonWasClicked = true;
        } catch (e) {
          console.warn('Could not store button click flag:', e);
        }
        // Don't prevent default - let OptiMonk do its redirect to ?DISCOUNTCODE URL
      }
    }, true); // Use capture phase to catch events early

  }

  /**
   * Set up variant change listener to prevent Shopify price updates when discount is active
   * Uses MutationObserver to detect and revert Shopify's price updates
   */
  setupVariantChangeListener() {
    if (!this.isProductPage) return;

    // Watch for Shopify price updates and restore discount display
    const priceWrapper = document.querySelector('.price-wrapper, #price-' + document.querySelector('[data-section]')?.dataset.section);
    if (!priceWrapper) return;

    const observer = new MutationObserver((mutations) => {
      // Only act if discount has been applied
      if (!this.discountCode || !this.discountPercent) {
        return;
      }

      // Check if price element was modified
      const priceModified = mutations.some(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const target = mutation.target;
          if (target.classList?.contains('price') || 
              target.closest?.('.price') || 
              target.querySelector?.('.price-item--regular')) {
            return true;
          }
        }
        return false;
      });

      if (priceModified) {
        // Small delay to ensure Shopify's update completes
        setTimeout(() => {
          // Check if discount display was removed by Shopify's update
          const existingDiscount = priceWrapper.querySelector('.discounted-price');
          const priceContainer = priceWrapper.querySelector('.price');
          const hasDiscountClass = priceContainer?.classList.contains('has-discount-applied');
          
          // If discount display is missing or was overwritten, restore it
          if (!existingDiscount || !hasDiscountClass) {
            // Get current price (to update originalPrice display)
            this.getCurrentProductPrice();
            if (this.originalPrice && this.discountedPrice) {
              // Reuse the exact same discounted price (no recalculation needed)
              // All variants have same price, so discounted price stays the same
              this.updatePriceDisplay();
            }
          }
        }, 100);
      }
    });

    // Start observing price wrapper for changes
    observer.observe(priceWrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  /**
   * Auto-apply discount when page loads with discount code in URL
   * This is triggered after OptiMonk redirects to product page with ?DISCOUNTCODE
   */
  autoApplyDiscount() {
    // Re-check URL in case code wasn't detected initially
    const codeFromURL = this.extractDiscountCodeFromURL();
    if (codeFromURL && !this.discountCode) {
      this.discountCode = codeFromURL;
    }

    if (!this.discountCode || !this.isProductPage) {
      return;
    }

    
    // Get current product price
    this.getCurrentProductPrice();
    
    if (!this.originalPrice) {
      console.warn('Could not determine product price for auto-apply');
      return;
    }
    
    // Query discount details and update display using cart API
    this.getActualDiscountAmount()
      .then(() => {
        this.updatePriceDisplay();
      })
      .catch((error) => {
        console.error('Failed to auto-apply discount:', error);
      });
  }

  /**
   * Handle discount refresh after OptiMonk interaction
   * Extracts discount code from URL parameter (?DISCOUNTCODE) when button is clicked
   * Works with any discount code format
   */
  handleDiscountRefresh() {
    if (!this.isProductPage) {
      console.warn('Not on a product page');
      return;
    }

    // Extract discount code from current URL parameter (format: ?DISCOUNTCODE)
    const codeFromURL = this.extractDiscountCodeFromURL();
    if (!codeFromURL) {
      console.warn('No discount code found in URL parameter');
      return;
    }

    // Set the discount code
    this.discountCode = codeFromURL;

    // Get current product price
    this.getCurrentProductPrice();
    
    if (!this.originalPrice) {
      console.warn('Could not determine product price');
      return;
    }
    
    // Query discount details and update display using cart API
    this.getActualDiscountAmount()
      .then(() => {
        this.updatePriceDisplay();
      })
      .catch((error) => {
        console.error('Failed to apply discount:', error);
      });
  }

  /**
   * Get current product price from the page
   */
  getCurrentProductPrice() {
    const priceElement = document.querySelector('.price-item--regular, .price .price-item');
    
    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      const priceMatch = priceText.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        this.originalPrice = parseFloat(priceMatch[0].replace(',', ''));
      }
    }
  }

  /**
   * Get actual discount amount (Cart API primary, pattern matching fallback)
   * 
   * The cart API method works for ANY discount code format (e.g., "20deal", "BRAZIL20", etc.)
   * because it queries Shopify's actual discount calculation, not pattern matching.
   * 
   * Pattern matching is only used as an unreliable fallback when cart API fails.
   */
  async getActualDiscountAmount() {
    // Method 1: Cart-based approach (PRIMARY - works for any discount code format)
    try {
      await this.cartBasedDiscountQuery();
      return Promise.resolve();
    } catch (error) {
      console.warn('Cart-based discount failed:', error.message);
    }
    
    // Method 2: Pattern matching (UNRELIABLE FALLBACK - may fail for codes like "BRAZIL20")
    const estimatedDiscount = this.estimateDiscountFromCode();
    if (estimatedDiscount) {
      console.warn('Using unreliable pattern matching fallback. Cart API should be primary method.');
      this.calculateDiscountedPrice(estimatedDiscount.value, estimatedDiscount.type);
      return Promise.resolve();
    }
    
    return Promise.reject('Could not determine discount amount');
  }

  /**
   * Cart-based discount detection (PRIMARY METHOD)
   * 
   * This method works for ANY discount code format because it:
   * 1. Temporarily adds the product to the cart
   * 2. Shopify automatically applies the discount (already in session from discount URL visit)
   * 3. Calculates the actual discounted price from cart data
   * 4. Restores the original cart state
   * 
   * Note: The discount should already be active in Shopify's session from when
   * the user visited the discount URL or clicked the email link. When we add
   * the product, Shopify will apply the discount automatically if it's active.
   */
  async cartBasedDiscountQuery() {
    if (!this.originalPrice) throw new Error('No original price available');
    
    // Step 1: Get current cart state to restore later
    const currentCart = await this.getCurrentCart();
    
    // Step 2: Get current product variant ID
    const variantId = this.getCurrentVariantId();
    if (!variantId) {
      throw new Error('Could not determine current product variant');
    }
    
    // Step 3: Add current product to cart temporarily
    const addResult = await this.addToCart(variantId, 1);
    
    // Step 4: Get cart (discount should auto-apply if discount URL was visited)
    const discountedCart = await this.getCurrentCart();
    
    // Step 5: Calculate actual discount amount
    const actualDiscount = this.calculateActualDiscount(addResult, discountedCart);
    
    // Step 6: Restore original cart state
    await this.restoreCartState(currentCart, variantId);
    
    if (actualDiscount) {
      this.discountedPrice = actualDiscount.discountedPrice;
      // Store discount percentage for variant persistence
      this.discountPercent = actualDiscount.discountPercent;
      return Promise.resolve();
    }
    
    throw new Error('Could not calculate discount from cart');
  }

  /**
   * Get current cart state
   */
  async getCurrentCart() {
    const response = await fetch('/cart.js');
    return await response.json();
  }

  /**
   * Get current product variant ID
   */
  getCurrentVariantId() {
    const selectors = [
      'input[name="id"]',
      '[data-variant-id]',
      '.product-form input[type="hidden"][name="id"]'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        const variantId = element.value || element.dataset.variantId;
        if (variantId) {
          return variantId;
        }
      }
    }
    
    return null;
  }

  /**
   * Add product to cart
   */
  async addToCart(variantId, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: quantity
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to add to cart: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Calculate actual discount from cart data
   */
  calculateActualDiscount(addResult, cartWithDiscount) {
    const originalItemPrice = addResult.price;
    const cartItems = cartWithDiscount.items || [];
    
    // Find our added item in the cart
    const ourItem = cartItems.find(item => item.variant_id == addResult.variant_id);
    
    if (ourItem) {
      const discountedItemPrice = ourItem.final_price || ourItem.price;
      const discountAmount = originalItemPrice - discountedItemPrice;
      
      if (discountAmount > 0) {
        // Convert from cents
        const originalPrice = originalItemPrice / 100;
        const discountedPrice = discountedItemPrice / 100;
        const discountPercent = (discountAmount / originalItemPrice) * 100;
        
        // Determine discount type based on calculated percentage
        // If discount is a round percentage (within 0.1%), it's likely a percentage discount
        const isRoundPercent = Math.abs(discountPercent - Math.round(discountPercent)) < 0.1;
        const discountType = isRoundPercent ? 'percentage' : 'fixed_amount';
        
        return {
          originalPrice: originalPrice,
          discountedPrice: discountedPrice,
          discountAmount: discountAmount / 100,
          discountType: discountType,
          discountPercent: Math.round(discountPercent)
        };
      }
    }
    
    return null;
  }

  /**
   * Restore cart to original state
   */
  async restoreCartState(originalCart, addedVariantId) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: addedVariantId,
          quantity: 0
        })
      });
    } catch (error) {
      console.warn('Could not remove test item from cart:', error);
    }
  }

  /**
   * Estimate discount from code pattern (fallback method - unreliable)
   * Note: This is a last resort fallback. Pattern matching cannot reliably determine
   * discount amounts for arbitrary codes (e.g., "BRAZIL20" vs "20deal").
   * The cart API method should be used as the primary approach.
   */
  estimateDiscountFromCode() {
    if (!this.discountCode) return null;
    
    const code = this.discountCode.toLowerCase();
    
    // Generic patterns that attempt to extract numbers from codes
    // These patterns are unreliable and may fail for many discount codes
    const patterns = [
      // Explicit patterns (most reliable if matched)
      { regex: /(\d+)percent/i, type: 'percentage' },     // "20percent" = 20% off
      { regex: /(\d+)%/i, type: 'percentage' },           // "20%" = 20% off
      { regex: /£(\d+)/i, type: 'fixed_amount' },         // "£25" = £25 off
      { regex: /(\d+)pounds?/i, type: 'fixed_amount' },    // "25pound" = £25 off
      { regex: /\$(\d+)/i, type: 'fixed_amount' },        // "$25" = $25 off
      // Generic patterns (less reliable - may incorrectly match codes like "BRAZIL20")
      { regex: /(\d+)off$/i, type: 'fixed_amount' },      // "10off" = £10 off (end of string)
      { regex: /save(\d+)/i, type: 'percentage' },        // "save15" = 15% off
      // Last resort: extract trailing numbers (very unreliable)
      // This could incorrectly interpret codes like "BRAZIL20" as 20% discount
      { regex: /(\d+)$/, type: 'percentage' }            // Any trailing number (assume percentage)
    ];
    
    for (const pattern of patterns) {
      const match = code.match(pattern.regex);
      if (match) {
        const value = parseInt(match[1]);
        if (value > 0 && value <= 100) { // Reasonable bounds
          return {
            value: value,
            type: pattern.type
          };
        }
      }
    }
    
    return null;
  }

  /**
   * Calculate discounted price based on discount type and value
   */
  calculateDiscountedPrice(value, type) {
    if (!this.originalPrice || !value) return;
    
    let discountAmount = 0;
    
    if (type === 'percentage') {
      discountAmount = (this.originalPrice * value) / 100;
      // Store discount percentage for variant persistence
      this.discountPercent = value;
    } else if (type === 'fixed_amount') {
      discountAmount = value;
      // Calculate percentage for fixed amount discounts
      this.discountPercent = (discountAmount / this.originalPrice) * 100;
    }
    
    this.discountedPrice = Math.max(0, this.originalPrice - discountAmount);
  }

  /**
   * Update the price display on the page
   */
  updatePriceDisplay() {
    if (!this.originalPrice || !this.discountedPrice) {
      console.warn('Missing price data for display update');
      return;
    }

    const priceWrapper = document.querySelector('.price-wrapper, #price-' + document.querySelector('[data-section]')?.dataset.section);
    
    if (!priceWrapper) {
      console.warn('Price wrapper not found');
      return;
    }

    const originalPriceElement = priceWrapper.querySelector('.price-item--regular');
    
    if (!originalPriceElement) {
      console.warn('Original price element not found');
      return;
    }

    // Remove any existing discount display (for variant changes)
    const existingDiscount = priceWrapper.querySelector('.discounted-price');
    if (existingDiscount) {
      const existingContainer = existingDiscount.closest('.price__regular');
      if (existingContainer && existingContainer !== originalPriceElement.closest('.price__regular')) {
        existingContainer.remove();
      }
    }

    // Remove has-discount-applied class to reset
    const priceContainer = originalPriceElement.closest('.price');
    if (priceContainer) {
      priceContainer.classList.remove('has-discount-applied');
    }

    // Reset original price styling first (in case of variant change)
    originalPriceElement.style.textDecoration = '';
    originalPriceElement.style.textDecorationThickness = '';
    originalPriceElement.style.color = '';

    // Add strikethrough and grey color to original price
    originalPriceElement.style.textDecoration = 'line-through';
    originalPriceElement.style.textDecorationThickness = '1px';
    originalPriceElement.style.color = '#727272';

    // Format discounted price
    const currency = this.getShopCurrency();
    const discountedPriceFormatted = this.formatPrice(this.discountedPrice, currency);

    // Create new price element with same structure as original
    const originalPriceContainer = originalPriceElement.closest('.price__regular');
    if (originalPriceContainer) {
      const discountedPriceContainer = originalPriceContainer.cloneNode(true);
      
      const discountedSpan = discountedPriceContainer.querySelector('.price-item--regular');
      discountedSpan.textContent = discountedPriceFormatted;
      discountedSpan.setAttribute('data-original-price', discountedPriceFormatted);
      discountedSpan.classList.add('discounted-price');
      discountedSpan.style.textDecoration = 'none';
      discountedSpan.style.color = 'inherit';
      
      // Update accessibility text
      const hiddenSpan = discountedPriceContainer.querySelector('.visually-hidden');
      if (hiddenSpan) {
        hiddenSpan.textContent = 'Sale price';
      }
      
      // Insert after original price
      originalPriceContainer.parentNode.insertBefore(discountedPriceContainer, originalPriceContainer.nextSibling);
      
      // Add class to price container for styling
      const priceContainer = originalPriceContainer.closest('.price');
      if (priceContainer) {
        priceContainer.classList.add('has-discount-applied');
      }
      
      // Store discount state for session persistence (after successful display update)
      this.storeDiscountState();
    }
  }

  /**
   * Store discount state for session persistence
   * Allows discount to be restored when user navigates back to product page
   */
  storeDiscountState() {
    if (!this.discountCode || !this.discountPercent || !this.discountedPrice) {
      return;
    }

    try {
      // Get current product handle to track which product this discount applies to
      const productHandle = this.getProductHandle();
      
      const discountState = {
        discountCode: this.discountCode,
        discountPercent: this.discountPercent,
        discountedPrice: this.discountedPrice,
        productHandle: productHandle, // Store which product this was applied to
        timestamp: Date.now()
      };
      sessionStorage.setItem('sokito_discount_state', JSON.stringify(discountState));
    } catch (e) {
      console.warn('Could not store discount state:', e);
    }
  }

  /**
   * Get current product handle from URL or page
   */
  getProductHandle() {
    // Try to extract from URL first
    const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }
    
    // Fallback: try to get from data attributes
    const productHandle = document.querySelector('[data-product-handle]')?.dataset?.productHandle ||
                         document.querySelector('[data-product]')?.dataset?.product;
    
    return productHandle || null;
  }

  /**
   * Restore discount state from session storage
   * Validates that discount applies to current product before restoring
   * Returns true if state was restored, false otherwise
   */
  async restoreDiscountState() {
    try {
      const stored = sessionStorage.getItem('sokito_discount_state');
      if (!stored) return false;

      const discountState = JSON.parse(stored);
      
      // Check if stored discount is still valid (within 30 minutes)
      if (Date.now() - discountState.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem('sokito_discount_state');
        return false;
      }

      // Quick check: Only try to restore if we're on the same product it was applied to
      const currentProductHandle = this.getProductHandle();
      if (discountState.productHandle && currentProductHandle && 
          discountState.productHandle !== currentProductHandle) {
        // We're on a different product - don't restore, but keep state stored
        return false;
      }

      // Restore discount code to validate
      const storedCode = discountState.discountCode;
      
      // Validate that this discount applies to the current product via cart API
      // This prevents applying discount to products not included in the discount
      const isValid = await this.validateDiscountForCurrentProduct(storedCode);
      
      if (!isValid) {
        // Only clear stored state if validation fails on the SAME product it was applied to
        // (meaning the discount is no longer valid)
        if (discountState.productHandle === currentProductHandle) {
          sessionStorage.removeItem('sokito_discount_state');
        }
        return false;
      }

      // Discount is valid for this product - restore the state
      this.discountCode = discountState.discountCode;
      this.discountPercent = discountState.discountPercent;
      this.discountedPrice = discountState.discountedPrice;
      
      // Wait for DOM to be ready, then restore the display
      setTimeout(() => {
        // Get current product price
        this.getCurrentProductPrice();
        
        if (!this.originalPrice || this.originalPrice <= 0) {
          return;
        }

        // Apply the discount display
        this.updatePriceDisplay();
      }, 500);
      
      return true;
    } catch (e) {
      console.warn('Could not restore discount state:', e);
      return false;
    }
  }

  /**
   * Validate that a discount code applies to the current product
   * Uses cart API to check if discount actually applies to this specific product
   */
  async validateDiscountForCurrentProduct(discountCode) {
    try {
      // Get current product price
      if (!this.originalPrice) {
        this.getCurrentProductPrice();
      }
      if (!this.originalPrice) {
        return false;
      }

      const variantId = this.getCurrentVariantId();
      if (!variantId) {
        return false;
      }

      // Temporarily add product to cart
      const currentCart = await this.getCurrentCart();
      const addResult = await this.addToCart(variantId, 1);
      
      // Get cart (discount will apply automatically if it's active and valid for this product)
      const discountedCart = await this.getCurrentCart();
      
      // Check if discount was actually applied to this product
      const actualDiscount = this.calculateActualDiscount(addResult, discountedCart);
      
      // Restore cart
      await this.restoreCartState(currentCart, variantId);
      
      // Return true only if:
      // 1. Discount was actually applied (discounted price < original)
      // 2. There was an actual discount amount
      return actualDiscount && 
             actualDiscount.discountedPrice < addResult.price / 100 &&
             actualDiscount.discountAmount > 0;
    } catch (e) {
      console.warn('Could not validate discount for product:', e);
      return false;
    }
  }

  /**
   * Get shop currency symbol
   */
  getShopCurrency() {
    const priceElement = document.querySelector('.price-item');
    if (priceElement) {
      const priceText = priceElement.textContent;
      if (priceText.includes('£')) return 'GBP';
      if (priceText.includes('$')) return 'USD';
      if (priceText.includes('€')) return 'EUR';
    }
    
    return 'GBP';
  }

  /**
   * Format price with currency
   */
  formatPrice(price, currency) {
    const symbols = {
      'GBP': '£',
      'USD': '$',
      'EUR': '€'
    };
    
    const symbol = symbols[currency] || '£';
    return `${symbol}${price.toFixed(2)}`;
  }
}

// Initialize when DOM is ready
let discountHandler;

function initDiscountHandler() {
  try {
    discountHandler = new DiscountPopupHandler();
    window.discountHandler = discountHandler;
  } catch (error) {
    console.error('Error initializing Discount Popup Handler:', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountHandler);
} else {
  initDiscountHandler();
}

// Initialize on Shopify section loads (for theme editor)
document.addEventListener('shopify:section:load', initDiscountHandler);

// Re-check for discount code when page becomes visible (only if button was clicked)
document.addEventListener('visibilitychange', () => {
  if (!document.hidden && window.discountHandler) {
    const buttonWasClicked = sessionStorage.getItem('sokito_discount_button_clicked') === 'true';
    if (buttonWasClicked) {
      // Re-check URL for discount code (in case URL changed via navigation)
      const codeFromURL = window.discountHandler.extractDiscountCodeFromURL();
      if (codeFromURL) {
        window.discountHandler.autoApplyDiscount();
        sessionStorage.removeItem('sokito_discount_button_clicked');
      }
    }
  }
});

// Also check on popstate (back/forward navigation) - only if button was clicked
window.addEventListener('popstate', () => {
  if (window.discountHandler) {
    const buttonWasClicked = sessionStorage.getItem('sokito_discount_button_clicked') === 'true';
    if (buttonWasClicked) {
      setTimeout(() => {
        window.discountHandler.autoApplyDiscount();
        sessionStorage.removeItem('sokito_discount_button_clicked');
      }, 300);
    }
  }
});

// Test functions for development
window.setDiscountCode = function(discountCode) {
  if (!discountCode) {
    console.warn('Please provide a discount code. Usage: setDiscountCode("YOURCODE")');
    return;
  }
  
  if (window.discountHandler) {
    window.discountHandler.discountCode = discountCode;
    window.discountHandler.storeDiscountCode(discountCode);
  } else {
    console.warn('Discount handler not available yet');
  }
};

window.clearDiscountCode = function() {
  if (window.discountHandler) {
    window.discountHandler.clearStoredDiscountCode();
    window.discountHandler.discountCode = null;
  } else {
    // Clear storage even if handler not initialized
    try {
      sessionStorage.removeItem('sokito_discount_code');
      sessionStorage.removeItem('sokito_discount_timestamp');
      localStorage.removeItem('sokito_discount_code');
      localStorage.removeItem('sokito_discount_timestamp');
    } catch (e) {
      console.warn('Could not clear discount code:', e);
    }
  }
};

window.simulateOptiMonkClick = function() {
  if (!window.discountHandler) {
    console.warn('Discount handler not initialized');
    return;
  }
  
  const fakeButton = document.createElement('button');
  fakeButton.className = 'discount-refresh';
  fakeButton.style.display = 'none';
  document.body.appendChild(fakeButton);
  fakeButton.click();
  
  setTimeout(() => {
    if (fakeButton.parentNode) {
      fakeButton.parentNode.removeChild(fakeButton);
    }
  }, 500);
};

// Backward compatibility alias
window.simulateOptinMonkClick = window.simulateOptiMonkClick;
